import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import api from "@/lib/api";
import PortalShell from "@/components/PortalShell";
import { LayoutDashboard, Ticket, Store, BarChart3, ShieldCheck, BadgeCheck, Megaphone, Key, BookOpen, Webhook, Link2, Handshake, Users, CreditCard, Radar, PiggyBank, Repeat, Trophy, Package, Wand2, HelpCircle, Bell, Scale, IdCard, LayoutGrid } from "lucide-react";
import MerchantOverview from "@/pages/merchant/Overview";
import MerchantCoupons from "@/pages/merchant/Coupons";
import CouponAssistant from "@/pages/merchant/CouponAssistant";
import MerchantStore from "@/pages/merchant/StoreProfile";
import MerchantAnalytics from "@/pages/merchant/Analytics";
import MerchantVerification from "@/pages/merchant/Verification";
import MerchantCampaigns from "@/pages/merchant/Campaigns";
import MerchantApiKeys from "@/pages/merchant/ApiKeys";
import MerchantDocs from "@/pages/merchant/Docs";
import MerchantDeveloperPortal from "@/pages/merchant/DeveloperPortal";
import MerchantWebhooks from "@/pages/merchant/Webhooks";
import ConversionVerification from "@/pages/merchant/ConversionVerification";
import MerchantAffiliates from "@/pages/merchant/Affiliates";
import AiIntegrations from "@/pages/merchant/integrations/AiIntegrations";
import MCPDraftReview from "@/pages/merchant/MCPDraftReview";
import MerchantBilling from "@/pages/merchant/Billing";
import MerchantTracking from "@/pages/merchant/Tracking";
import MerchantCashback from "@/pages/merchant/Cashback";
import MerchantInsights from "@/pages/merchant/Insights";
import MerchantStorefront from "@/pages/merchant/Storefront";
import MerchantStorefronts from "@/pages/merchant/Storefronts";
import MerchantReferralProgram from "@/pages/merchant/ReferralProgram";
import MerchantFAQ from "@/pages/merchant/FAQ";
import MerchantProducts from "@/pages/merchant/Products";
import MerchantCheckoutVerification from "@/pages/merchant/CheckoutVerification";
import MerchantCampaignReview from "@/pages/merchant/CampaignReview";
import MerchantStorefrontSetupGuide from "@/pages/merchant/StorefrontSetupGuide";
import MerchantSettings from "@/pages/merchant/Settings";
import MerchantLegalAgreements from "@/pages/merchant/LegalAgreements";
import MerchantSponsoredCashback from "@/pages/merchant/SponsoredCashback";
import LegalGate from "@/components/legal/LegalGate";

const links = [
  { to: "/merchant", end: true, label: "Overview", icon: LayoutDashboard, testid: "merch-nav-overview" },
  { to: "/merchant/storefronts", label: "Storefronts", icon: LayoutGrid, testid: "merch-nav-storefronts", badge: "New" },
  { to: "/merchant/storefront", label: "My storefront", icon: Store, testid: "merch-nav-storefront" },
  { to: "/merchant/products", label: "Products", icon: Package, testid: "merch-nav-products", badge: "New" },
  { to: "/merchant/campaigns", label: "Offers", icon: Megaphone, testid: "merch-nav-campaigns" },
  { to: "/merchant/checkout-verification", label: "Checkout verification", icon: ShieldCheck, testid: "merch-nav-checkout-verification", badge: "New" },
  { to: "/merchant/affiliates", label: "Affiliates", icon: Users, testid: "merch-nav-affiliates" },
  { to: "/merchant/coupons", label: "Legacy coupons", icon: Ticket, testid: "merch-nav-coupons" },
  { to: "/merchant/analytics", label: "Analytics", icon: BarChart3, testid: "merch-nav-analytics" },
  { to: "/merchant/insights", label: "Insights", icon: Trophy, testid: "merch-nav-insights" },
  { to: "/merchant/tracking", label: "Tracking", icon: Radar, testid: "merch-nav-tracking" },
  { to: "/merchant/cashback", label: "Cashback", icon: PiggyBank, testid: "merch-nav-cashback" },
  { to: "/merchant/api-keys", label: "API keys", icon: Key, testid: "merch-nav-api-keys" },
  { to: "/merchant/integrations/ai", label: "AI Integrations", icon: Wand2, testid: "merch-nav-ai-integrations", badge: "Beta" },
  { to: "/merchant/webhooks", label: "Webhooks", icon: Webhook, testid: "merch-nav-webhooks" },
  { to: "/merchant/conversion-verification", label: "Conversion Verification", icon: Link2, testid: "merch-nav-conversion-verification" },
  { to: "/merchant/docs", label: "Developer docs", icon: BookOpen, testid: "merch-nav-docs" },
  { to: "/merchant/billing", label: "Billing", icon: CreditCard, testid: "merch-nav-billing" },
  { to: "/merchant/referrals", label: "Refer & earn", icon: Handshake, testid: "merch-nav-referrals", badge: "New" },
  { to: "/merchant/store", label: "Store profile", icon: IdCard, testid: "merch-nav-store" },
  { to: "/merchant/verification", label: "Business verification", icon: BadgeCheck, testid: "merch-nav-verification" },
  { to: "/merchant/settings", label: "Settings & alerts", icon: Bell, testid: "merch-nav-settings" },
  { to: "/merchant/terms", label: "Terms & Agreements", icon: Scale, testid: "merch-nav-legal" },
  { to: "/merchant/faq", label: "FAQ & help", icon: HelpCircle, testid: "merch-nav-faq" },
];

const sponsoredLink = { to: "/merchant/sponsored-cashback", label: "Sponsored Cashback", icon: Repeat, testid: "merch-nav-sponsored-cashback", badge: "Preview" };

function useNavLinks() {
  const [sponsored, setSponsored] = useState(false);
  useEffect(() => { api.get("/sponsored-cashback/flags").then((r) => setSponsored(!!r.data.merchant)).catch(() => {}); }, []);
  if (!sponsored) return links;
  const i = links.findIndex((l) => l.to === "/merchant/cashback") + 1;
  return [...links.slice(0, i), sponsoredLink, ...links.slice(i)];
}

export default function MerchantPortal() {
  const navLinks = useNavLinks();
  return (
    <PortalShell title="Merchant" subtitle="Portal" links={navLinks} testid="merchant-portal" variant="burgundy">
      <LegalGate actor="MERCHANT">
      <Routes>
        <Route index element={<MerchantOverview />} />
        <Route path="storefronts" element={<MerchantStorefronts />} />
        <Route path="storefront" element={<MerchantStorefront />} />
        <Route path="products" element={<MerchantProducts />} />
        <Route path="campaigns" element={<MerchantCampaigns />} />
        <Route path="campaigns/:id/review" element={<MerchantCampaignReview />} />
        <Route path="storefront-setup" element={<MerchantStorefrontSetupGuide />} />
        <Route path="checkout-verification" element={<MerchantCheckoutVerification />} />
        <Route path="affiliates" element={<MerchantAffiliates />} />
        <Route path="coupons" element={<MerchantCoupons />} />
        <Route path="coupons/ai" element={<CouponAssistant />} />
        <Route path="coupons/review/:draftId" element={<MCPDraftReview />} />
        <Route path="integrations/ai" element={<AiIntegrations />} />
        <Route path="analytics" element={<MerchantAnalytics />} />
        <Route path="insights" element={<MerchantInsights />} />
        <Route path="tracking" element={<MerchantTracking />} />
        <Route path="cashback" element={<MerchantCashback />} />
        <Route path="sponsored-cashback" element={<MerchantSponsoredCashback />} />
        <Route path="api-keys" element={<MerchantApiKeys />} />
        <Route path="developer" element={<MerchantDeveloperPortal />} />
        <Route path="webhooks" element={<MerchantWebhooks />} />
        <Route path="conversion-verification" element={<ConversionVerification />} />
        <Route path="docs" element={<MerchantDocs />} />
        <Route path="billing" element={<MerchantBilling />} />
        <Route path="referrals" element={<MerchantReferralProgram />} />
        <Route path="faq" element={<MerchantFAQ />} />
        <Route path="store" element={<MerchantStore />} />
        <Route path="verification" element={<MerchantVerification />} />
        <Route path="settings" element={<MerchantSettings />} />
        <Route path="terms" element={<MerchantLegalAgreements />} />
        <Route path="legal" element={<Navigate to="/merchant/terms" replace />} />
      </Routes>
      </LegalGate>
    </PortalShell>
  );
}
